import React from 'react';

import { PasswordStrengthType, ValidationService } from 'us-web-services';
import { useSharedTranslation } from '../hooks/useSharedTranslation';

interface Props {
  password: string;
  namespace?: string;
}

const filledByStrength: Record<PasswordStrengthType, number> = {
  empty: 0,
  weak: 1,
  medium: 2,
  good: 3,
  strong: 4,
};

const colorsByStrength = (strength: PasswordStrengthType): { bar: string; text: string } => {
  if (strength === 'strong' || strength === 'good') {
    return { bar: 'bg-green-500', text: 'text-green-600' };
  }
  if (strength === 'medium') {
    return { bar: 'bg-yellow-500', text: 'text-yellow-600' };
  }
  return { bar: 'bg-red-500', text: 'text-red-600' };
};

export const PasswordStrengthIndicator = ({ password, namespace }: Props): JSX.Element | null => {
  const { t } = useSharedTranslation(namespace);
  if (!password) return null;
  const strength = ValidationService.passwordStrength(password).type;
  const filled = filledByStrength[strength];
  const { bar, text } = colorsByStrength(strength);
  return (
    <div id='password-strength-indicator'>
      <p className={`${text} text-sm font-semibold mb-1`}>
        {t(`components.passwordChange.strength.${strength}`)}
      </p>
      <div className='flex gap-1'>
        {[0, 1, 2, 3].map((i) => (
          <span
            key={i}
            className={`block h-2.5 flex-1 rounded ${i < filled ? bar : 'bg-neutral-200'}`}
          />
        ))}
      </div>
    </div>
  );
};
